import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Card, TabBar, Badge } from '@thegpvc/ui';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { text, cx } from '../styles/theme';
import { formatDuration, formatTimestamp, formatUsd } from '../utils/format';
import { errorMessage } from '../utils/errorMessage';
import type { ProjectReadiness, TimePeriod } from '../types/api';

const periods = [
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
  { id: 'all', label: 'All Time' },
];

function burden(p: ProjectReadiness): number {
  return (p.frictionMoments ?? 0) / Math.max(p.sessions ?? 0, 1);
}

function ProjectCard({ p }: { p: ProjectReadiness }) {
  const name = p.project || 'unknown';
  const b = burden(p);
  const hasFriction = (p.frictionMoments ?? 0) > 0;
  // A flagged project with many sessions can round to "0.0", which reads as
  // "no friction" while the badge is warning-colored — show an honest floor.
  const burdenLabel = b.toFixed(1) === '0.0' ? '<0.1' : b.toFixed(1);

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/sessions?project=${encodeURIComponent(name)}`}
            className={cx('text-base font-semibold hover:underline', text.primary)}
          >
            {name}
          </Link>
          {hasFriction ? (
            <Badge variant={b >= 1 ? 'error' : 'warning'}>
              {burdenLabel} friction/session
            </Badge>
          ) : (
            <Badge variant="success">no friction</Badge>
          )}
        </div>

        <div className={cx('mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm', text.secondary)}>
          <span>
            <span className={cx('font-medium tabular-nums', text.primary)}>{p.sessions ?? 0}</span> sessions
          </span>
          <span>{formatDuration(p.totalDurationMs ?? 0)} active</span>
          {(p.costUsd ?? 0) > 0 && <span>{formatUsd(p.costUsd ?? 0)}</span>}
          {(p.wasteUsd ?? 0) > 0 && (
            <span className="text-rose-600 dark:text-rose-400">{formatUsd(p.wasteUsd ?? 0)} waste</span>
          )}
        </div>

        <div className={cx('mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm', text.muted)}>
          <span>last active {formatTimestamp(p.lastActive ?? '0')}</span>
          {hasFriction && (
            <Link
              to={`/sessions?project=${encodeURIComponent(name)}&friction=true`}
              className={text.link}
            >
              {p.frictionSessions ?? 0} friction {p.frictionSessions === 1 ? 'session' : 'sessions'} →
            </Link>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

/**
 * Projects answers "where does the agent work well or badly?": one card per
 * project, ranked by friction burden (the server orders most-agent-hostile
 * first — honest ranking, no synthetic grade). Cost/waste appear as priced
 * data lands.
 */
export function Projects() {
  const [period, setPeriod] = useState<TimePeriod>('30days');
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['projectReadiness', period],
    queryFn: () => api.listProjectReadiness(period),
    placeholderData: keepPreviousData,
  });

  const projects = data?.projects ?? [];

  return (
    <PageContainer>
      <title>Dixie :: Projects</title>
      <PageHeader title="Projects" count={projects.length || undefined} isFetching={isFetching && !isLoading}>
        <TabBar
          variant="pill"
          items={periods}
          activeId={period}
          onChange={(id: string) => setPeriod(id as TimePeriod)}
        />
      </PageHeader>

      <p className={cx('text-sm', text.secondary)}>
        Ranked by friction burden — the most agent-hostile projects first. Fixing a repo-level
        cause (a CLAUDE.md gap, a flaky harness) pays off across every session in it.
      </p>

      {error && <Alert variant="error">Failed to load projects: {errorMessage(error)}</Alert>}

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-gray-100 dark:bg-navy-800" />
          ))}
        </div>
      )}

      {!isLoading && !error && projects.length === 0 && (
        <p className={cx('py-10 text-center text-sm', text.secondary)}>
          No projects with sessions in this period.
        </p>
      )}

      {!isLoading && projects.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {projects.map((p) => (
            <ProjectCard key={p.project || 'unknown'} p={p} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
